/**
 * MobX store for the admin "RMAs JSON Download & Upload" card: shared portal selection,
 * JSON download via POST /rms-api/admin-download-open-rmas-json, and JSON upload via
 * POST /rms-api/admin-upload-open-rmas-json.
 */
import { makeAutoObservable, runInAction } from "mobx";
import axios from "axios";
import AdminMigrateGlobalEMerchantsService, {
    DownloadOpenRmasResult,
} from "../../../../services/api/admin-migrate-global-e-merchants-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import { IShop } from "../../../../models/User";
import { isSet } from "../../../../models/model-types-methods";

class RmasJsonDownloadUploadStore {
    private _selectedShop: IShop | null | undefined = null;
    private _file: File | null = null;
    private _isProcessing = false;
    private _infoMessage: string | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): AdminMigrateGlobalEMerchantsService {
        return rootContainer.get<AdminMigrateGlobalEMerchantsService>(TYPE.AdminMigrateGlobalEMerchantsService);
    }

    public get portal(): IShop | null | undefined {
        return this._selectedShop;
    }

    public get file(): File | null {
        return this._file;
    }

    public get isProcessing(): boolean {
        return this._isProcessing;
    }

    public get infoMessage(): string | null {
        return this._infoMessage;
    }

    public setPortal(shop: IShop | null | undefined): void {
        this._selectedShop = shop;
        this._infoMessage = null;
    }

    public setJsonFile(file: File): void {
        const name = file.name.toLowerCase();
        if (!name.endsWith(".json")) {
            return;
        }
        this._file = file;
        this._infoMessage = null;
    }

    public async download(): Promise<void> {
        if (!isSet(this._selectedShop) || this._isProcessing) {
            return;
        }

        this._isProcessing = true;
        this._infoMessage = null;

        try {
            const result = await this.service.downloadOpenRmas(this._selectedShop.id);
            if (result.kind === "message") {
                runInAction(() => {
                    this._infoMessage = result.message;
                });
                return;
            }
            this.triggerBlobDownload(result);
        } catch (error: unknown) {
            let message = "Download failed";
            if (axios.isAxiosError(error) && error.response?.data) {
                const d = error.response.data as { message?: string; error?: string; errors?: { message: string } };
                const responseMessage = String(d.message ?? d.error ?? d.errors?.message ?? "");
                message = message + (responseMessage ? `: ${responseMessage}` : ".");
            } else if (error instanceof Error) {
                message = message + (error.message ? `: ${error.message}` : ".");
            }
            runInAction(() => {
                this._infoMessage = message;
            });
        } finally {
            runInAction(() => {
                this._isProcessing = false;
            });
        }
    }

    public async upload(): Promise<void> {
        if (!isSet(this._selectedShop) || !this._file || this._isProcessing) {
            return;
        }

        this._isProcessing = true;
        this._infoMessage = null;

        try {
            const data = await this.service.uploadOpenRmasJson(this._selectedShop.id, this._file);
            runInAction(() => {
                this._infoMessage = `${data.message} (attempt_id: ${data.attempt_id})`;
            });
        } catch (error: unknown) {
            let message = "Upload failed";
            if (axios.isAxiosError(error) && error.response?.data) {
                const d = error.response.data as { message?: string; error?: string; errors?: { message: string } };
                const responseMessage = String(d.message ?? d.error ?? d.errors?.message ?? "");
                message = message + (responseMessage ? `: ${responseMessage}` : ".");
            } else if (error instanceof Error) {
                message = message + (error.message ? `: ${error.message}` : ".");
            }
            runInAction(() => {
                this._infoMessage = message;
            });
        } finally {
            runInAction(() => {
                this._isProcessing = false;
            });
        }
    }

    private triggerBlobDownload(result: Extract<DownloadOpenRmasResult, { kind: "file" }>): void {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }
}

export default new RmasJsonDownloadUploadStore();
